import { EventNotification, EventNotificationSortByTerm, SortOrder } from '@bechtel/types';

export interface SortType {
  sortColumn: EventNotificationSortByTerm;
  sortOrder: SortOrder;
}

export interface SyncRowProps {
  rowData: EventNotification;
  onCompleted: (syncData) => void;
  onError: (error) => void;
}

// Mapping from AG Grid column fields to EventNotificationSortByTerm
export const COLUMN_TO_SORT_TERM_MAP: Record<string, EventNotificationSortByTerm> = {
  entityTypeId: EventNotificationSortByTerm.ENTITY_TYPE_ID,
  status: EventNotificationSortByTerm.STATUS,
  errorTypeId: EventNotificationSortByTerm.ERROR_TYPE_ID,
  notificationTypeId: EventNotificationSortByTerm.NOTIFICATION_TYPE_ID,
  createdAt: EventNotificationSortByTerm.CREATED_AT,
  retryCount: EventNotificationSortByTerm.RETRY_COUNT,
  nextScheduleAt: EventNotificationSortByTerm.NEXT_SCHEDULE_AT,
};

// Simple sort model interface matching AG Grid's column state
export interface SortModelItem {
  colId: string;
  sort: 'asc' | 'desc' | null;
}

export const mapAgGridSortToGraphQL = (sortModel: SortModelItem[]): SortType | null => {
  if (!sortModel || sortModel.length === 0) {
    return null;
  }

  // Get the first sort (single column sorting only)
  const firstSort = sortModel[0];
  const sortTerm = COLUMN_TO_SORT_TERM_MAP[firstSort.colId];
  
  if (!sortTerm || !firstSort.sort) {
    return null; // Column not supported for server-side sorting
  }

  return {
    sortColumn: sortTerm,
    sortOrder: firstSort.sort === 'asc' ? SortOrder.ASC : SortOrder.DESC,
  };
};

// Reverse mapping from EventNotificationSortByTerm to column field
export const SORT_TERM_TO_COLUMN_MAP: Record<EventNotificationSortByTerm, string> = {
  [EventNotificationSortByTerm.ENTITY_TYPE_ID]: 'entityTypeId',
  [EventNotificationSortByTerm.STATUS]: 'status',
  [EventNotificationSortByTerm.ERROR_TYPE_ID]: 'errorTypeId',
  [EventNotificationSortByTerm.NOTIFICATION_TYPE_ID]: 'notificationTypeId',
  [EventNotificationSortByTerm.CREATED_AT]: 'createdAt',
  [EventNotificationSortByTerm.RETRY_COUNT]: 'retryCount',
  [EventNotificationSortByTerm.NEXT_SCHEDULE_AT]: 'nextScheduleAt',
  [EventNotificationSortByTerm.UPDATED_AT]: 'updatedAt', // Not used in current colDefs but keeping for completeness
};

export const mapGraphQLSortToAgGrid = (sortType: SortType): { colId: string; sort: 'asc' | 'desc' } | null => {
  const colId = SORT_TERM_TO_COLUMN_MAP[sortType.sortColumn];
  if (!colId) {
    return null;
  }
  
  return {
    colId,
    sort: sortType.sortOrder === SortOrder.ASC ? 'asc' : 'desc'
  };
};
